/**
 * MagicBI Design System - Main Index
 * 
 * Central export for the complete design system including
 * design tokens, themes, components, and utilities.
 */

// Import all dependencies first
import {
  lightThemeColors,
  darkThemeColors,
  rawColors,
  typography,
  spacing,
  semanticSpacing,
  componentSpacing,
  layoutSpacing,
  shadows,
  darkShadows,
  effectShadows,
  borderRadius,
  borderWidth,
  borderStyle,
  semanticBorderRadius,
  componentBorders,
  borderColors,
  duration,
  easing,
  transitions,
  animations,
  breakpointValues,
  mediaQueries,
  deviceQueries,
  zIndex,
} from './tokens';

import { lightTheme, darkTheme } from './themes';

import {
  Display,
  Heading,
  Text,
  Label,
  Caption,
  Code,
  H1,
  H2,
  H3,
  H4,
  H5,
  H6,
  Button,
  Input,
  Container,
  Grid,
  GridItem,
  Flex,
  Stack,
  Box,
  Spacer,
  Header,
  Section,
} from './components/primitives';

import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  CardImage,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FeatureCard,
  TestimonialCard,
  StatsCard,
} from './components/composite';

import {
  themeToCSSCustomProperties,
  applyCSSCustomProperties,
  getSystemDarkMode,
  validateTheme,
  debugTheme,
} from './utils/theme-utils';

// Design Tokens
export * from './tokens';

// Themes
export * from './themes';

// Types
export * from './types/theme';

// Utilities
export * from './utils/theme-utils';

// Theme Provider and Hooks
export { ThemeProvider } from '../providers/ThemeProvider';
export {
  useThemeMode,
  useThemeColors,
  useThemeSpacing,
  useThemeTypography,
  useThemeShadows,
  useThemeBorders,
  useThemeTransitions,
  useThemeBreakpoints,
} from '../providers/ThemeProvider';

// Additional hooks
export { useTheme } from '../hooks/useTheme';

// Primitive Components
export * from './components/primitives';

// Composite Components
export * from './components/composite';

// Component types are already exported via the regular export * statements above

// Design System Bundle
export const MagicBIDesignSystem = {
  // Design tokens
  tokens: {
    colors: { lightThemeColors, darkThemeColors, rawColors },
    typography,
    spacing: { spacing, semanticSpacing, componentSpacing, layoutSpacing },
    shadows: { shadows, darkShadows, effectShadows },
    borders: { borderRadius, borderWidth, borderStyle, semanticBorderRadius, componentBorders, borderColors },
    transitions: { duration, easing, transitions, animations },
    breakpoints: { breakpointValues, mediaQueries, deviceQueries },
    zIndex,
  },
  
  // Themes
  themes: { lightTheme, darkTheme },
  
  // Components (primitives)
  primitives: {
    Typography: { Display, Heading, Text, Label, Caption, Code, H1, H2, H3, H4, H5, H6 },
    Button,
    Input,
    Layout: { Container, Grid, GridItem, Flex, Stack, Box, Spacer },
    Header,
    Section,
  },
  
  // Components (composite)
  composite: {
    Card: { Card, CardHeader, CardBody, CardFooter, CardImage },
    Modal: { Modal, ModalHeader, ModalBody, ModalFooter },
    FeatureCard,
    TestimonialCard,
    StatsCard,
  },
  
  // Utilities
  utils: {
    themeToCSSCustomProperties,
    applyCSSCustomProperties,
    getSystemDarkMode,
    validateTheme,
    debugTheme,
  },
} as const;

export default MagicBIDesignSystem;